"use client"

import { useEffect, useRef } from "react"
import { toast } from "sonner"
import { APP_VERSION, decideUpdateAction } from "@/lib/app-version"
import { logger } from "@/lib/logger"

const POLL_INTERVAL_MS = 5 * 60 * 1000
const TOAST_ID = "app-version-reload"
// sessionStorage flag holding the version we last auto-reloaded for — the
// loop guard fed into decideUpdateAction. Session-scoped on purpose: a later
// deploy (different version) may auto-reload again.
const AUTO_RELOADED_FOR_KEY = "app-version-auto-reloaded-for"

const log = logger.scope("version-poller")

function readAutoReloadedFor(): string | null {
  try {
    return window.sessionStorage.getItem(AUTO_RELOADED_FOR_KEY)
  } catch {
    return null
  }
}

function autoReload(toVersion: string): void {
  try {
    window.sessionStorage.setItem(AUTO_RELOADED_FOR_KEY, toVersion)
  } catch {
    // sessionStorage unavailable — the loop guard cannot persist across the
    // reload, so skip auto-reload entirely rather than risk a reload loop.
    // The normal visible-toast path still covers the update.
    return
  }
  log.info("Auto-reloading hidden tab to apply new deploy", {
    from: APP_VERSION,
    to: toVersion,
  })
  window.location.reload()
}

/**
 * Polls /api/version and applies the update policy (lib/app-version.ts →
 * decideUpdateAction) when a newer deploy is live. Replaces the dead PWA
 * skipWaiting/reloadOnOnline behavior — this is how tablets learn about new
 * deploys now:
 * - hidden tab → auto-reload silently (once per detected version)
 * - visible tab → persistent "Reload now" toast; if it goes untapped, the
 *   moment the tablet goes idle (visibilitychange → hidden) it self-updates.
 * Checks every 5 minutes, plus immediately when the tab regains visibility or
 * the network comes back (tablets sleep between meal services — visibility is
 * the check that actually fires in practice). Mounted once in app/layout.tsx.
 */
export function VersionPoller() {
  const prompted = useRef(false)
  // Newer version detected but not yet applied — lets the hidden-visibility
  // handler auto-reload without refetching.
  const pendingVersion = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const apply = (fetched: string) => {
      const action = decideUpdateAction({
        current: APP_VERSION,
        fetched,
        visibilityState: document.visibilityState,
        alreadyReloadedFor: readAutoReloadedFor(),
      })
      if (action === "reload") {
        autoReload(fetched)
        return
      }
      if (action !== "prompt") return
      pendingVersion.current = fetched
      if (prompted.current) return
      prompted.current = true
      toast.info("App update available", {
        id: TOAST_ID,
        description:
          "A new version has been deployed. Reload to get the latest fixes — saved form drafts are kept.",
        duration: Infinity,
        action: { label: "Reload now", onClick: () => window.location.reload() },
      })
    }

    const check = async () => {
      if (prompted.current) return
      try {
        const res = await fetch("/api/version", { cache: "no-store" })
        if (!res.ok) return
        const body: unknown = await res.json()
        const fetched = (body as { version?: unknown } | null)?.version
        if (cancelled || typeof fetched !== "string") return
        apply(fetched)
      } catch {
        // Offline or transient network error — the next poll retries.
      }
    }

    const interval = setInterval(check, POLL_INTERVAL_MS)
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void check()
        return
      }
      // Tab just went hidden with an untapped update pending — self-apply.
      const pending = pendingVersion.current
      if (cancelled || pending === null) return
      const action = decideUpdateAction({
        current: APP_VERSION,
        fetched: pending,
        visibilityState: "hidden",
        alreadyReloadedFor: readAutoReloadedFor(),
      })
      if (action === "reload") autoReload(pending)
    }
    document.addEventListener("visibilitychange", onVisibilityChange)
    window.addEventListener("online", check)
    void check()

    return () => {
      cancelled = true
      clearInterval(interval)
      document.removeEventListener("visibilitychange", onVisibilityChange)
      window.removeEventListener("online", check)
    }
  }, [])

  return null
}
